'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { buildActivityTrailCustomMeta } from '@/components/providers/activity-trail-meta';
import { type MessageDataPart } from '@/components/providers/data-renderers-provider';
import { resolveStructuredText } from '@/lib/utils/message';

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: ThreadMessageLike['content'];
  createdAt?: Date;
  updatedAt?: Date;
  locale?: string;
  // Explicit duration override. If omitted but both timestamps are set,
  // duration is derived from `updatedAt - createdAt`.
  durationMs?: number;
  // Step keys are tool-call ids for tool steps and `__streaming__:N` for the
  // N-th text part — matching the activity-trail's internal step id format.
  // `gapBeforeMs` is optional: when omitted the trail derives the row anchor
  // from `latestCompletedAt - durationMs` instead.
  stepTimings?: Record<string, { startedAt: number; completedAt: number; gapBeforeMs?: number }>;
  // Host-baked custom data rendered after the bubble by the matching component
  // in `dataComponents`. Baking it onto the message (vs a render callback over
  // host-side async state) lets the read-only load gate cover it — no late pop-in.
  dataParts?: MessageDataPart[];
}

export interface ConversationThread {
  id: string;
  title?: string;
}

interface ReadOnlyRuntimeProviderProps extends PropsWithChildren {
  /** Load the thread list (called on mount) */
  onLoadThreads: () => Promise<ConversationThread[]>;
  /** Lazy-load messages for a thread when selected */
  onLoadMessages?: (threadId: string) => Promise<ConversationMessage[]>;
  /**
   * Called once when the initial data is loaded (threads + first thread's
   * messages). Use this to hide a loading skeleton in the host app —
   * the UI is ready to be revealed.
   */
  onReady?: () => void;
  /**
   * Called whenever the active thread changes. Use this to sync external
   * UI (e.g. metadata panels) with the currently selected thread.
   */
  onActiveThreadChange?: (threadId: string) => void;
}

const ReadOnlyLoadingContext = createContext(false);

export function useReadOnlyLoading(): boolean {
  return useContext(ReadOnlyLoadingContext);
}

const MessageLocaleMapContext = createContext<Record<string, string>>({});

export function useMessageLocaleMap(): Record<string, string> {
  return useContext(MessageLocaleMapContext);
}

const NOOP_ASYNC = async () => {};
const IDENTITY = (msg: ThreadMessageLike) => msg;
const EMPTY_STORE = {
  messages: [] as ThreadMessageLike[],
  isRunning: false,
  isDisabled: true,
  convertMessage: IDENTITY,
  onNew: NOOP_ASYNC,
};

// When a tool input fails Mastra's validator, the model retries inside the
// same step and Vertex/Gemini reuses the original toolCallId, so two
// `tool-call` parts can share an id. @assistant-ui/tap throws on duplicate
// keys ("Duplicate key toolCallId-..." in tapResources), so we collapse to
// the last occurrence (the corrected/successful retry).
function dedupeToolCallsByCallId<T extends { type?: string; toolCallId?: string }>(
  parts: T[],
): T[] {
  const seen = new Set<string>();
  const reversed: T[] = [];
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part.type === 'tool-call' && part.toolCallId) {
      if (seen.has(part.toolCallId)) continue;
      seen.add(part.toolCallId);
    }
    reversed.push(part);
  }
  return reversed.reverse();
}

function resolveContent(content: ThreadMessageLike['content']): ThreadMessageLike['content'] {
  if (typeof content === 'string') return resolveStructuredText(content);
  if (!Array.isArray(content)) return content;

  return dedupeToolCallsByCallId(content).map(part =>
    part.type === 'text' ? { ...part, text: resolveStructuredText(part.text) } : part,
  );
}

function convertMessages(messages: ConversationMessage[]): ThreadMessageLike[] {
  return messages.map(msg => {
    const custom = buildActivityTrailCustomMeta(msg);
    const dataParts = msg.dataParts?.length ? msg.dataParts : undefined;
    const mergedCustom =
      custom || dataParts ? { ...custom, ...(dataParts && { dataParts }) } : undefined;
    return {
      id: msg.id,
      role: msg.role,
      content: resolveContent(msg.content),
      createdAt: msg.createdAt,
      ...(msg.role === 'assistant' && {
        status: { type: 'complete' as const, reason: 'stop' as const },
      }),
      ...(mergedCustom && { metadata: { custom: mergedCustom } }),
    };
  });
}

// ---------------------------------------------------------------------------
// Inner component – owns the external store runtime and lazy-loading state.
// ---------------------------------------------------------------------------

function ReadOnlyRuntimeCore({
  onLoadThreads,
  onLoadMessages,
  onReady,
  onActiveThreadChange,
  children,
}: ReadOnlyRuntimeProviderProps) {
  const [threads, setThreads] = useState<ConversationThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState('');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loadedThreadId, setLoadedThreadId] = useState('');
  const [loadingCount, setLoadingCount] = useState(0);

  // Loading is derived from three independent conditions:
  //  1. loadingCount > 0  — an async operation (threads or messages) is in flight.
  //  2. !initialReady      — the very first data hasn't loaded yet.
  //  3. thread mismatch    — activeThreadId changed but the new thread's messages
  //                          haven't been committed yet (loadedThreadId lags).
  //
  // Condition 3 kicks in *synchronously* inside handleSwitchThread (which sets
  // activeThreadId), closing the gap that would otherwise exist between the
  // state update and the useEffect that starts the fetch. This prevents a
  // flash of stale messages between thread switches.
  const [initialReady, setInitialReady] = useState(false);
  const loading =
    loadingCount > 0 || !initialReady || (activeThreadId !== loadedThreadId && !!onLoadMessages);
  const onReadyRef = useLatest(onReady);
  const loadIdRef = useRef(0);
  const threadLoadIdRef = useRef(0);
  // True while a thread-list load is waiting to signal "ready" (its first
  // thread's messages aren't shown yet). Tied to the LIST lifecycle, not the
  // active-thread one, so onReady fires on initial load + each reactive reload
  // but NOT on internal user thread-switches.
  const pendingReadyRef = useRef(false);
  const activeThreadIdRef = useLatest(activeThreadId);
  const loadedThreadIdRef = useLatest(loadedThreadId);

  const fireReady = useCallback(() => {
    if (!pendingReadyRef.current) return;
    pendingReadyRef.current = false;
    setInitialReady(true);
    onReadyRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onReadyRef is a stable ref from useLatest
  }, []);

  const onActiveThreadChangeRef = useLatest(onActiveThreadChange);

  // Load the thread list on mount AND whenever it changes (the host recreates
  // `onLoadThreads` when its filtered results change). Reloading in place updates
  // the external store instead of forcing the host to remount the whole runtime —
  // a full @assistant-ui/tap re-init was costing 150–240ms per filter change.
  useEffect(() => {
    const loadId = ++threadLoadIdRef.current;
    pendingReadyRef.current = true;
    setLoadingCount(c => c + 1);
    onLoadThreads()
      .then(loaded => {
        if (threadLoadIdRef.current !== loadId) return;
        setThreads(loaded);
        if (loaded.length === 0) {
          setActiveThreadId('');
          setMessages([]);
          fireReady();
          return;
        }
        const firstId = loaded[0].id;
        const activeChanged = firstId !== activeThreadIdRef.current;
        if (activeChanged) {
          // New active thread — the message effect loads it and fires ready.
          setActiveThreadId(firstId);
          onActiveThreadChangeRef.current?.(firstId);
        }
        // Fire ready now when no message load will do it for us: no message
        // loader at all, or the active thread is unchanged and already loaded.
        if (!onLoadMessages || (!activeChanged && loadedThreadIdRef.current === firstId)) {
          fireReady();
        }
      })
      .catch(err => {
        if (threadLoadIdRef.current !== loadId) return;
        console.error('[ReadOnlyRuntime] Thread load failed:', err);
        fireReady();
      })
      .finally(() => setLoadingCount(c => c - 1));
  }, [
    onLoadThreads,
    onLoadMessages,
    fireReady,
    activeThreadIdRef,
    loadedThreadIdRef,
    onActiveThreadChangeRef,
  ]);

  // Load messages whenever active thread changes. The actual `setMessages([])`
  // that forces a clean unmount of the prior thread's message subtree happens
  // synchronously in `handleSwitchThread` (alongside `setActiveThreadId`), not
  // here — if we cleared from inside this effect, an `onLoadMessages` that
  // resolves synchronously (cached) would let React batch the clear and the
  // populate into one render, skipping the empty commit and reintroducing the
  // out-of-bounds part-subscription bug.
  useEffect(() => {
    if (!activeThreadId || !onLoadMessages) return;

    const loadId = ++loadIdRef.current;
    setLoadingCount(c => c + 1);

    onLoadMessages(activeThreadId)
      .then(msgs => {
        if (loadIdRef.current === loadId) {
          setMessages(msgs);
          // Delay by one frame so the runtime propagates new messages to
          // subscribers before the overlay hides (threadMismatch → false).
          requestAnimationFrame(() => {
            if (loadIdRef.current === loadId) setLoadedThreadId(activeThreadId);
          });
        }
      })
      .catch(err => {
        console.error('[ReadOnlyRuntime] Load failed:', err);
        if (loadIdRef.current === loadId) setLoadedThreadId(activeThreadId);
      })
      .finally(() => {
        setLoadingCount(c => c - 1);
        // Delay `fireReadyOnce` by one frame so `useExternalStoreRuntime`
        // has time to propagate the updated store to subscribers (Thread).
        // fireReady is idempotent so calling it on every non-stale
        // completion is safe and avoids edge cases where the initial load
        // becomes stale due to a rapid thread switch.
        if (loadIdRef.current === loadId) {
          requestAnimationFrame(() => fireReady());
        }
      });
  }, [activeThreadId, onLoadMessages, fireReady]);

  const threadMessages = useMemo(() => convertMessages(messages), [messages]);

  // Accumulate locale entries across thread switches. The assistant-ui runtime
  // propagates new messages to subscribers one render after the context update,
  // so old message IDs briefly coexist with the new locale map. Keeping
  // previous entries prevents a locale flash during that transitional render.
  const localeMapRef = useRef<Record<string, string>>({});
  const messageLocaleMap = useMemo(() => {
    for (const msg of messages) {
      if (msg.locale) localeMapRef.current[msg.id] = msg.locale;
    }
    return { ...localeMapRef.current };
  }, [messages]);

  const handleSwitchThread = useCallback(
    (id: string) => {
      if (threads.some(t => t.id === id)) {
        // Clear synchronously with the activeThreadId change — both updates
        // batch into one render with messages=[] AND the new threadId, which
        // forces ThreadPrimitive.Messages to unmount the prior thread's per-
        // index providers before the load effect populates the new messages.
        // Without this synchronous clear, a cached/synchronous onLoadMessages
        // resolution would let React collapse the clear+populate into a single
        // render — defeating the unmount and resurfacing the out-of-bounds
        // part-subscription bug that this provider is meant to avoid.
        setMessages([]);
        setActiveThreadId(id);
        onActiveThreadChangeRef.current?.(id);
      }
    },
    [threads, onActiveThreadChangeRef],
  );

  // Two-render initialization: useExternalStoreRuntime needs to see a different
  // store reference on the second render to properly initialize _mainThreadId
  // (workaround for @assistant-ui ExternalStoreThreadListRuntimeCore bug).
  const [initialized, setInitialized] = useState(false);
  useLayoutEffect(() => setInitialized(true), []);

  const store = useMemo(() => {
    if (!initialized || !activeThreadId) return EMPTY_STORE;

    return {
      messages: threadMessages,
      isRunning: false,
      isDisabled: true,
      convertMessage: IDENTITY,
      onNew: NOOP_ASYNC,
      adapters: {
        threadList: {
          threadId: activeThreadId,
          threads: threads.map(t => ({
            id: t.id,
            status: 'regular' as const,
            title: t.title,
          })),
          onSwitchToThread: handleSwitchThread,
          onSwitchToNewThread: NOOP_ASYNC,
        },
      },
    };
  }, [initialized, threadMessages, activeThreadId, threads, handleSwitchThread]);

  const runtime = useExternalStoreRuntime(store);

  if (!initialized) return null;

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <MessageLocaleMapContext.Provider value={messageLocaleMap}>
        <ReadOnlyLoadingContext.Provider value={loading}>
          {children}
        </ReadOnlyLoadingContext.Provider>
      </MessageLocaleMapContext.Provider>
    </AssistantRuntimeProvider>
  );
}

// ---------------------------------------------------------------------------
// Public component – skips SSR to avoid hydration mismatches.
// ---------------------------------------------------------------------------

export function ReadOnlyRuntimeProvider({
  onLoadThreads,
  onLoadMessages,
  onReady,
  onActiveThreadChange,
  children,
}: ReadOnlyRuntimeProviderProps) {
  // Skip SSR — useExternalStoreRuntime causes hydration mismatches
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return (
    <ReadOnlyRuntimeCore
      onLoadThreads={onLoadThreads}
      onLoadMessages={onLoadMessages}
      onReady={onReady}
      onActiveThreadChange={onActiveThreadChange}
    >
      {children}
    </ReadOnlyRuntimeCore>
  );
}
